<template>
  <section class="information-accordion-module-component">
    <div class="inner-container">
      <h2
        class="text-[40px] text-balanced text-center"
        v-html="data.section_title"></h2>

      <div class="accordion-container">
        <div
          v-for="(item, index) in data.collapsible_items"
          :key="index"
          class="accordion-item">
          <div class="accordion-header">
            <h3
              class="header-5 text-balanced normal-case"
              @click="toggle(index)"
              v-html="item.title"></h3>
            <button
              class="accordion-title-toggle"
              :class="{ active: open[index] }"
              @click="toggle(index)">
              <img src="@/assets/img/caret-down-progressive.svg" />
            </button>
          </div>
          <Vue3SlideUpDown
            v-model="open[index]"
            :class="[
              open[index] ? 'open' : 'closed',
              {
                'has-internal-accordions': internalAccordions(item).length,
              },
            ]"
            class="accordion-content">
            <div class="divider"></div>
            <div
              class="content"
              v-html="item.content"></div>

            <div
              v-if="internalAccordions(item).length"
              class="internal-accordion-container">
              <div
                v-for="(internalItem, internalIndex) in internalAccordions(
                  item
                )"
                :key="`internal-accordion-${index}-${internalIndex}`"
                class="internal-accordion-item">
                <button
                  type="button"
                  class="internal-accordion-header"
                  :aria-expanded="isInternalOpen(index, internalIndex)"
                  @click="toggleInternal(index, internalIndex)">
                  <span
                    class="header-5 text-balanced normal-case"
                    v-html="internalItem.title"></span>
                  <img
                    :class="{
                      active: isInternalOpen(index, internalIndex),
                    }"
                    src="@/assets/img/caret-down-progressive.svg"
                    alt="" />
                </button>

                <Vue3SlideUpDown
                  :model-value="isInternalOpen(index, internalIndex)"
                  class="internal-accordion-content">
                  <div
                    class="content internal-content"
                    v-html="internalItem.content"></div>
                </Vue3SlideUpDown>
              </div>
            </div>
          </Vue3SlideUpDown>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "InformationAccordionComponent",
    "CLASSNAME": "information-accordion-module-component",
    "URL": "http://vuevitewp.local/test"
   }
  #ENDMETA */

/*
 *   Insert your logic here
 */

import { ref, onMounted } from 'vue'
import Vue3SlideUpDown from 'vue3-slide-up-down'

const props = defineProps(['data'])

const accordionItems = ref(props.data.collapsible_items)
const open = ref([])
const internalOpen = ref({})

const toggle = (idx) => {
  open.value[idx] = !open.value[idx]
}

const internalAccordions = (item) =>
  item?.internal_accordions || item?.internalAccordions || []

const internalAccordionKey = (itemIndex, internalIndex) =>
  `${itemIndex}-${internalIndex}`

const isInternalOpen = (itemIndex, internalIndex) =>
  Boolean(internalOpen.value[internalAccordionKey(itemIndex, internalIndex)])

const toggleInternal = (itemIndex, internalIndex) => {
  const key = internalAccordionKey(itemIndex, internalIndex)

  internalOpen.value = {
    ...internalOpen.value,
    [key]: !internalOpen.value[key],
  }
}

onMounted(() => {
  accordionItems.value.forEach(() => {
    open.value.push(false)
  })
})
</script>

<style lang="scss" scoped>
.information-accordion-module-component {
  @apply w-full overflow-hidden;

  .inner-container {
    @apply flex flex-col gap-8 lg:gap-14;

    .accordion-container {
      @apply flex flex-col gap-2;

      .accordion-item {
        @apply z-2 flex flex-col overflow-hidden rounded-[10px] bg-white px-[30px];

        .accordion-header {
          @apply flex items-center gap-2 justify-between py-3;

          h3 {
            @apply cursor-pointer;
          }
        }

        .accordion-title-toggle {
          @apply flex h-8 w-8 max-w-[32px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-functional transition-transform duration-300 ease-in-out;

          &.active {
            transform: rotate(180deg);
          }
        }

        .accordion-content {
          @apply overflow-hidden;

          .divider {
            @apply w-[66px] h-[3px] bg-progressive mb-6 mt-2 relative;

            &:after {
              @apply content-[''] absolute top-0 w-1/2 right-0 bg-balanced h-full;
            }
          }

          .content {
            @apply pb-10;

            &:deep() {
              .pre-ul {
                @apply pl-2 lg:pl-10;
              }

              strong {
                @apply font-bold;
              }

              p {
                @apply mb-5;
              }

              a {
                @apply underline;
              }

              ol {
                @apply list-decimal pl-9 mb-5 lg:pl-[80px];
              }

              ul {
                @apply list-disc pl-9 mb-5 lg:pl-[80px];
              }

              li {
                &::marker {
                  @apply text-progressive;
                }

                @apply text-[18px] mb-2;

                strong {
                  @apply font-bold;
                }
              }
            }
          }

          .internal-accordion-container {
            @apply mx-auto flex w-full flex-col gap-2 pb-10 lg:pl-[120px] lg:pr-3;

            .internal-accordion-item {
              @apply overflow-hidden rounded-[10px] bg-white;

              .internal-accordion-header {
                @apply flex w-full cursor-pointer items-center bg-functional justify-between gap-4 px-6 py-3 text-left;

                img {
                  @apply h-auto w-[15px] shrink-0 transition-transform duration-300 ease-in-out;

                  &.active {
                    @apply rotate-180;
                  }
                }
              }

              .internal-accordion-content {
                @apply overflow-hidden;

                .internal-content {
                  @apply bg-functional px-6 pt-2;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
